import * as React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
    LayoutDashboard,
    TrendingUp,
    Boxes,
    ShoppingBag,
    Users,
    Building2,
    FileText,
    Package,
    Truck,
    Receipt,
    Wallet,
    Sparkles,
    MapPin,
    Settings,
    Activity,
    User,
    LogOut,
    ChevronRight,
    Zap,
    CreditCard,
    ChevronDown,
    ChevronsUpDown,
    BadgeCheck,
    Bell,
    Calendar,
} from "lucide-react";

import {
    Sidebar,
    SidebarContent,
    SidebarFooter,
    SidebarGroup,
    SidebarGroupContent,
    SidebarGroupLabel,
    SidebarHeader,
    SidebarMenu,
    SidebarMenuButton,
    SidebarMenuItem,
} from "@/components/ui/sidebar";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
    DropdownMenuGroup,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { useAuth, useAuthActions } from "@/stores/authStore";
import { useSubscriptionStore } from "@/stores/subscriptionStore";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";

export function AppSidebar({ ...props }) {
    const location = useLocation();
    const navigate = useNavigate();
    const { user } = useAuth();
    const { logout } = useAuthActions();
    const { t } = useTranslation();
    const subscription = useSubscriptionStore(state => state.subscription);
    const fetchSubscription = useSubscriptionStore(state => state.fetchSubscription);

    React.useEffect(() => {
        fetchSubscription();
    }, [fetchSubscription]);

    const getPlanName = () => {
        if (!subscription?.planDetails) return 'Free Tier';
        return subscription.planDetails.name || 'Free Tier';
    };

    const navigationItems = [
        {
            category: t('sidebar.menu.overview'),
            items: [
                { path: "/app/dashboard", label: t('sidebar.items.dashboard'), icon: LayoutDashboard },
                { path: "/app/analytics", label: t('sidebar.items.analytics'), icon: TrendingUp },
            ],
        },
        {
            // Core business operations: profile + sales-side entities
            category: t('sidebar.menu.business'),
            items: [
                { path: "/app/daily-summary", label: t('sidebar.items.daily_summary', 'Daily Summary'), icon: Calendar },
                { path: "/app/inventory", label: t('sidebar.items.inventory'), icon: Boxes },
                { path: "/app/orders", label: t('sidebar.items.orders'), icon: ShoppingBag },
                { path: "/app/customers", label: t('sidebar.items.customers'), icon: Users },
                { path: "/app/business-settings", label: t('sidebar.items.business_profile'), icon: Building2 }
            ],
        },
        {
            // Purchasing & payables: invoices grouped with purchases and suppliers
            category: t('sidebar.menu.procurement', 'Purchasing'),
            items: [
                { path: "/app/invoices", label: t('sidebar.items.invoices', 'Invoices'), icon: FileText },
                { path: "/app/purchases", label: t('sidebar.items.purchases'), icon: Package },
                { path: "/app/suppliers", label: t('sidebar.items.suppliers'), icon: Truck },
            ],
        },
        {
            category: t('sidebar.menu.finance'),
            items: [
                { path: "/app/gst", label: t('sidebar.items.tax_gst'), icon: Receipt },
                { path: "/app/billing", label: t('sidebar.items.billing'), icon: Wallet },
                { path: "/app/subscription", label: t('sidebar.items.subscription'), icon: Sparkles },
            ],
        },
        {
            category: t('sidebar.menu.system'),
            items: [
                { path: "/app/locations", label: t('sidebar.items.locations'), icon: MapPin },
                { path: "/app/settings", label: t('sidebar.items.settings'), icon: Settings },
            ],
        },
    ];

    const handleLogout = async () => {
        try {
            await logout();
        } catch (error) {
            console.error("Logout failed:", error);
        }
    };

    return (
        <Sidebar variant="floating" collapsible="icon" {...props}>
            <SidebarHeader className="h-24 flex items-center px-6 pt-4 group-data-[collapsible=icon]:px-0 group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:h-14">
                <Link to="/app/dashboard" className="flex items-center gap-2.5 overflow-visible group-data-[collapsible=icon]:gap-0 group-data-[collapsible=icon]:justify-center w-full">
                    <div className="w-8 h-8 bg-white rounded-lg flex items-center justify-center shadow-lg shadow-slate-900/10 transition-transform hover:scale-105 group-data-[collapsible=icon]:h-10 group-data-[collapsible=icon]:w-10">
                        <span className="text-slate-900 font-bold text-lg leading-none pb-0.5">S</span>
                    </div>
                    <div className="flex flex-col group-data-[collapsible=icon]:hidden">
                        <span className="text-xl font-bold text-white tracking-tight">
                            soma.
                        </span>
                    </div>
                </Link>
            </SidebarHeader>
            <SidebarContent className="px-4 py-4 group-data-[collapsible=icon]:px-2">
                {navigationItems.map((group) => (
                    <SidebarGroup key={group.category} className="mb-6">
                        <SidebarGroupLabel className="text-[11px] font-bold text-slate-500 uppercase tracking-widest px-2 mb-3 group-data-[collapsible=icon]:hidden">
                            {group.category}
                        </SidebarGroupLabel>
                        <SidebarGroupContent>
                            <SidebarMenu className="gap-1.5">
                                {group.items.map((item) => {
                                    const isActive = location.pathname.startsWith(item.path);
                                    return (
                                        <SidebarMenuItem key={item.path}>
                                            <SidebarMenuButton
                                                asChild
                                                isActive={isActive}
                                                tooltip={item.label}
                                                className={cn(
                                                    "h-11 rounded-lg px-3.5 gap-3.5 transition-all duration-200 group-data-[collapsible=icon]:!size-10 group-data-[collapsible=icon]:!p-0 group-data-[collapsible=icon]:!justify-center",
                                                    isActive
                                                        ? "bg-[#1E293B]/50 text-[#10B981] shadow-none font-bold translate-x-1"
                                                        : "text-slate-400 font-medium hover:text-white hover:bg-white/5 transition-colors"
                                                )}
                                            >
                                                <Link to={item.path}>
                                                    <item.icon
                                                        size={20}
                                                        strokeWidth={isActive ? 2.5 : 2}
                                                        className={cn(
                                                            "shrink-0 transition-transform duration-200 group-active:scale-95",
                                                            isActive && "scale-105"
                                                        )}
                                                    />
                                                    <span className="group-data-[collapsible=icon]:hidden">{item.label}</span>
                                                </Link>
                                            </SidebarMenuButton>
                                        </SidebarMenuItem>
                                    );
                                })}
                            </SidebarMenu>
                        </SidebarGroupContent>
                    </SidebarGroup>
                ))}
            </SidebarContent>

            <SidebarFooter className="p-2 border-t border-slate-100/5 group-data-[collapsible=icon]:p-1">
                <SidebarMenu>
                    <SidebarMenuItem>
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <SidebarMenuButton
                                    size="lg"
                                    className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground w-full justify-start gap-3 px-2 py-5 hover:bg-white/10 transition-colors group-data-[collapsible=icon]:!size-10 group-data-[collapsible=icon]:!p-0 group-data-[collapsible=icon]:justify-center"
                                >
                                    <Avatar className="h-9 w-9 rounded-lg border border-white/20 group-data-[collapsible=icon]:h-8 group-data-[collapsible=icon]:w-8">
                                        <AvatarFallback className="bg-slate-800 text-white font-bold text-xs rounded-lg">
                                            {user?.name?.charAt(0) || "A"}
                                        </AvatarFallback>
                                    </Avatar>
                                    <div className="grid flex-1 text-left text-sm leading-tight group-data-[collapsible=icon]:hidden">
                                        <span className="truncate font-bold text-white tracking-tight">
                                            {user?.name || "Administrator"}
                                        </span>
                                        <span className="truncate text-xs text-slate-300 font-medium">
                                            {user?.email || "admin@soma.tech"}
                                        </span>
                                    </div>
                                    <ChevronsUpDown className="ml-auto size-4 text-slate-400 group-data-[collapsible=icon]:hidden" />
                                </SidebarMenuButton>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent
                                className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg"
                                side="bottom"
                                align="end"
                                sideOffset={4}
                            >
                                <DropdownMenuLabel className="p-0 font-normal">
                                    <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
                                        <Avatar className="h-8 w-8 rounded-lg">
                                            <AvatarFallback className="rounded-lg">
                                                {user?.name?.charAt(0) || "A"}
                                            </AvatarFallback>
                                        </Avatar>
                                        <div className="grid flex-1 text-left text-sm leading-tight">
                                            <span className="truncate font-semibold">{user?.name || "Administrator"}</span>
                                            <span className="truncate text-xs">{user?.email || "admin@soma.tech"}</span>
                                        </div>
                                    </div>
                                </DropdownMenuLabel>
                                <DropdownMenuSeparator />
                                <DropdownMenuGroup>
                                    <div className="px-2 py-1.5">
                                        <Badge variant="outline" className="w-full justify-center py-1.5 font-medium border-black-100 text-black-700 bg-black-50/50 rounded-full shadow-sm">
                                            {getPlanName()}
                                        </Badge>
                                    </div>
                                    {getPlanName() === 'Free Tier' && (
                                        <DropdownMenuItem onClick={() => navigate('/app/subscription')}>
                                            <Sparkles className="mr-2 h-4 w-4" />
                                            {t('sidebar.footer.upgrade_pro')}
                                        </DropdownMenuItem>
                                    )}
                                </DropdownMenuGroup>
                                <DropdownMenuSeparator />
                                <DropdownMenuGroup>
                                    <DropdownMenuItem onClick={() => navigate('/app/settings')}>
                                        <BadgeCheck className="mr-2 h-4 w-4" />
                                        {t('sidebar.footer.account')}
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => navigate('/app/billing')}>
                                        <CreditCard className="mr-2 h-4 w-4" />
                                        {t('sidebar.footer.billing')}
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => navigate('/app/settings')}>
                                        <Bell className="mr-2 h-4 w-4" />
                                        {t('sidebar.footer.notifications')}
                                    </DropdownMenuItem>
                                </DropdownMenuGroup>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem onClick={handleLogout}>
                                    <LogOut className="mr-2 h-4 w-4" />
                                    {t('sidebar.footer.logout')}
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    </SidebarMenuItem>
                </SidebarMenu>
            </SidebarFooter>
        </Sidebar>
    );
}
